import React from 'react';
import IconWrapper from '../../components/IconWrapper';
import {
  expensePolicyStatusConstants,
  expenseInvoicesStatusConstants,
  expensePaymentMethodConstants,
  expenseReviewStatusConstants,
  expenseTypesConstants,
  mccCategoriesConstants,
} from '../../constants';
import {Tag, Tooltip} from 'antd';
import {
  Invoice2Icon,
  InvoicePendingIcon,
  InvoiceSuggestIcon,
  WireIcon,
  WithLessCreditCardIcon,
  WithLessOutsideIcon
} from '../../icons';
import {
  AdvertisingServicesIcon,
  AirlinesIcon,
  AthleticFieldsCommercialSportsProfessionalSportsClubsSportsPromotersIcon,
  BooksPeriodicalsAndNewspapersIcon,
  BusinessServicesNotElsewhereClassifiedIcon,
  CommercialArtGraphicsPhotographyIcon,
  ComputerNetworkInformationServicesIcon,
  ComputerProgrammingDataProcessingAndIntegratedSystemDesignServicesIcon,
  ComputerSoftwareStoresIcon,
  ConsultingManagementAndPublicRelationsServicesIcon,
  CourierServiceAirAndGroundIcon,
  DepartmentStoresIcon,
  DigitalGoodsIcon,
  DirectMarketingCombinationCatalogAndRetailMerchantIcon,
  ElectronicsSalesIcon,
  EmploymentAgenciesAndTemporaryHelpServicesIcon,
  EquipmentFurnitureAndHomeFurnishingsStoresIcon,
  FinesIcon,
  FloristsIcon,
  GroceryStoresSupermarketsIcon,
  HotelsIcon,
  MiscellaneousAndSpecialtyRetailStoresIcon,
  MiscellaneousFoodStoresIcon,
  MiscellaneousGeneralMerchandiseStoresIcon,
  MiscellaneousPublishingAndPrintingIcon,
  OtherIcon,
  PassengerRailwaysIcon,
  PhotographicStudiosIcon,
  ProfessionalServicesIcon,
  RealEstateAgentsAndManagersRentalsIcon,
  RecreationServicesIcon,
  RestaurantsIcon,
  SchoolsAndEducationalServicesIcon,
  SoftwareIcon,
  SouvenirShopsIcon,
  SubscriptionMerchantsIcon,
  TaxicabsIcon,
  TaxIcon,
  TelecommunicationEquipmentIcon,
  TelecommunicationServicesIcon,
  TransportationSuburbanIcon,
  TravelAgenciesAndTourOperatorsIcon,
  VarietyStoresIcon
} from '../../icons/mccCategories';
import {alertActions} from '../../state/actions/alert.actions';
import {amountHelpers} from './amount.helpers';
import {objectHelpers} from './object.helpers';
import {transactionsHelpers} from './transactions.helpers';
import {vendorsHelpers} from './vendors.helpers';
import routes from '../../routes/routes.json';
import {EXPENSES_PAGE_SEARCH_KEYS} from '../../pages/ExpensesPage/expensesPage.constants';

const expensePolicyStatuses = {
  [expensePolicyStatusConstants.MISSING_INFO]: {
    key: 'expensePolicyStatuses.missing_info',
  },
  [expensePolicyStatusConstants.OUT_OF_POLICY]: {
    key: 'expensePolicyStatuses.out_of_policy',
  },
};

const expenseReviewStatuses = {
  [expenseReviewStatusConstants.APPROVED]: {
    backgroundColor: '#52C41A33',
    color: '#52C41A',
    key: 'expenseReviewStatuses.approved',
  },
  [expenseReviewStatusConstants.IN_REVIEW]: {
    key: 'expenseReviewStatuses.in_review',
  },
  [expenseReviewStatusConstants.REJECTED]: {
    backgroundColor: '#FEE4E2',
    color: '#FF4D4F',
    key: 'expenseReviewStatuses.rejected',
  },
};


export const expensesHelpers = {
  getGroupedExpenses: (expenses) => {
    const daysList = {};
    const groupedExpenses = [];
    let lastAdded;

    // sort expenses by created_at
    expenses = expenses.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    // Calculate total price for each month
    expenses.forEach(({ created_at, price }) => {
      const date = created_at.slice(0, 7);
      daysList[date] = { price: (daysList[date]?.price || 0) + Number(price || 0) };
    });

    // Add grouping rows and transactions
    expenses.forEach(exp => {
      const date = exp.created_at.slice(0, 7);
      if (lastAdded !== date) {
        groupedExpenses.push({
          id: date,
          isMonthGrouping: true,
          created_at: date,
          ...daysList[date],
        });
        lastAdded = date;
      }
      groupedExpenses.push(exp);
    });

    return transactionsHelpers.additionalTransactionsGrouping(groupedExpenses, 'isMonthGrouping');
  },
  getPolicyStatusLabel: (status, t) => {
    const option = expensePolicyStatuses[status];
    if (!option) return '';
    return <Tag style={{backgroundColor: '#FFF3DC', borderColor: '#FAAD14', color: '#FAAD14'}}>{t?.(option.key)}</Tag>;
  },
  getReviewStatusLabel: (status, t) => {
    const option = expenseReviewStatuses[status];
    if (!option) return '';
    return <Tag style={{backgroundColor: option.backgroundColor, borderColor: option.color, color: option.color}}>{t?.(option.key)}</Tag>;
  },
  getExpenseCategoryIcon: (value) => {
    return {
      [mccCategoriesConstants.ADVERTISING_SERVICES]: <AdvertisingServicesIcon />,
      [mccCategoriesConstants.AIRLINES]: <AirlinesIcon />,
      [mccCategoriesConstants.ATHLETIC_FIELDS_COMMERCIAL_SPORTS_PROFESSIONAL_SPORTS_CLUBS_SPORTS_PROMOTERS]: <AthleticFieldsCommercialSportsProfessionalSportsClubsSportsPromotersIcon />,
      [mccCategoriesConstants.BOOKS_PERIODICALS_AND_NEWSPAPERS]: <BooksPeriodicalsAndNewspapersIcon />,
      [mccCategoriesConstants.BUSINESS_SERVICES_NOT_ELSEWHERE_CLASSIFIED]: <BusinessServicesNotElsewhereClassifiedIcon />,
      [mccCategoriesConstants.COMMERCIAL_ART_GRAPHICS_PHOTOGRAPHY]: <CommercialArtGraphicsPhotographyIcon />,
      [mccCategoriesConstants.COMPUTER_NETWORK_INFORMATION_SERVICES]: <ComputerNetworkInformationServicesIcon />,
      [mccCategoriesConstants.COMPUTER_PROGRAMMING_DATA_PROCESSING_AND_INTEGRATED_SYSTEM_DESIGN_SERVICES]: <ComputerProgrammingDataProcessingAndIntegratedSystemDesignServicesIcon />,
      [mccCategoriesConstants.COMPUTER_SOFTWARE_STORES]: <ComputerSoftwareStoresIcon />,
      [mccCategoriesConstants.CONSULTING_MANAGEMENT_AND_PUBLIC_RELATIONS_SERVICES]: <ConsultingManagementAndPublicRelationsServicesIcon />,
      [mccCategoriesConstants.COURIER_SERVICES_AIR_AND_GROUND]: <CourierServiceAirAndGroundIcon />,
      [mccCategoriesConstants.DEPARTMENT_STORES]: <DepartmentStoresIcon />,
      [mccCategoriesConstants.DIGITAL_GOODS]: <DigitalGoodsIcon />,
      [mccCategoriesConstants.DIRECT_MARKETING_COMBINATION_CATALOG_AND_RETAIL_MERCHANT]: <DirectMarketingCombinationCatalogAndRetailMerchantIcon />,
      [mccCategoriesConstants.DIRECT_MARKETING_CONTINUITY_SUBSCRIPTION_MERCHANTS]: <SubscriptionMerchantsIcon />,
      [mccCategoriesConstants.ELECTRONICS_SALES]: <ElectronicsSalesIcon />,
      [mccCategoriesConstants.EMPLOYMENT_AGENCIES_AND_TEMPORARY_HELP_SERVICES]: <EmploymentAgenciesAndTemporaryHelpServicesIcon />,
      [mccCategoriesConstants.EQUIPMENT_FURNITURE_AND_HOME_FURNISHINGS_STORES]: <EquipmentFurnitureAndHomeFurnishingsStoresIcon />,
      [mccCategoriesConstants.FINES]: <FinesIcon />,
      [mccCategoriesConstants.FLORISTS]: <FloristsIcon />,
      [mccCategoriesConstants.GROCERY_STORES_SUPERMARKETS]: <GroceryStoresSupermarketsIcon />,
      [mccCategoriesConstants.HOTELS]: <HotelsIcon />,
      [mccCategoriesConstants.MISCELLANEOUS_AND_SPECIALTY_RETAIL_STORES]: <MiscellaneousAndSpecialtyRetailStoresIcon />,
      [mccCategoriesConstants.MISCELLANEOUS_FOOD_STORES]: <MiscellaneousFoodStoresIcon />,
      [mccCategoriesConstants.MISCELLANEOUS_GENERAL_MERCHANDISE_STORES]: <MiscellaneousGeneralMerchandiseStoresIcon />,
      [mccCategoriesConstants.MISCELLANEOUS_PUBLISHING_AND_PRINTING]: <MiscellaneousPublishingAndPrintingIcon />,
      [mccCategoriesConstants.PASSENGER_RAILWAYS]: <PassengerRailwaysIcon />,
      [mccCategoriesConstants.PHOTOGRAPHIC_STUDIOS]: <PhotographicStudiosIcon />,
      [mccCategoriesConstants.PROFESSIONAL_SERVICES]: <ProfessionalServicesIcon />,
      [mccCategoriesConstants.REAL_ESTATE_AGENTS_AND_MANAGERS_RENTALS]: <RealEstateAgentsAndManagersRentalsIcon />,
      [mccCategoriesConstants.RECREATION_SERVICES]: <RecreationServicesIcon />,
      [mccCategoriesConstants.RESTAURANTS]: <RestaurantsIcon />,
      [mccCategoriesConstants.SCHOOLS_AND_EDUCATIONAL_SERVICES]: <SchoolsAndEducationalServicesIcon />,
      [mccCategoriesConstants.SOFTWARE]: <SoftwareIcon />,
      [mccCategoriesConstants.SOUVENIR_SHOPS]: <SouvenirShopsIcon />,
      [mccCategoriesConstants.TAXICABS]: <TaxicabsIcon />,
      [mccCategoriesConstants.TAX_PAYMENTS]: <TaxIcon />,
      [mccCategoriesConstants.TELECOMMUNICATION_EQUIPMENT]: <TelecommunicationEquipmentIcon />,
      [mccCategoriesConstants.TELECOMMUNICATION_SERVICES]: <TelecommunicationServicesIcon />,
      [mccCategoriesConstants.TRANSPORTATION_SUBURBAN]: <TransportationSuburbanIcon />,
      [mccCategoriesConstants.TRAVEL_AGENCIES_AND_TOUR_OPERATORS]: <TravelAgenciesAndTourOperatorsIcon />,
      [mccCategoriesConstants.VARIETY_STORES]: <VarietyStoresIcon />,
    }[value] ?? <OtherIcon />;
  },
  getExpenseApprovals: (expense) => (expense?.approval || []).filter(review => review.approver?.email !== 'any_admin'),
  getExpenseApprovalReviews: (expense, employeeEmail) => {
    const canQuickApprove = expensesHelpers.canQuickApprove({expense, employeeEmail});
    return canQuickApprove ? [] : expensesHelpers.getExpenseApprovals(expense);
  },
  getExpenseDetailsUrl: (expense) => {
    if (!expense?.id) return null;

    const reviewStatusTabs = {
      [expenseReviewStatusConstants.APPROVED]: 'reviewed',
      [expenseReviewStatusConstants.REJECTED]: 'reviewed',
      [expenseReviewStatusConstants.IN_REVIEW]: 'requested',
    };

    const tab = reviewStatusTabs[expense?.review_status];
    return `${routes.expensesList}?${EXPENSES_PAGE_SEARCH_KEYS.SELECTED_EXPENSE_ID}=${expense.id}${tab ? `#${tab}` : ''}`;
  },

  getExpensePrice: (expense) => {
    if (!expense || typeof expense !== 'object') return null;

    const price = expense?.price;
    return amountHelpers.getAmountWithCurrencyCode(price > 0 ? -price : price);
  },

  getExpenseVendorNameWithLink: (expense, variant = 'text') => {
    if (!expense || typeof expense !== 'object') return null;
    return vendorsHelpers.getVendorNameWithLink({
      vendor_id: expense?.vendor_local_id,
      name: expense?.vendor_local_name || expense?.vendor,
    }, variant);
  },

  getExpenseStatusLabel: (expense, t) => {
    const reviewStatus = expense?.review_status;
    if ([expenseReviewStatusConstants.APPROVED, expenseReviewStatusConstants.REJECTED].includes(reviewStatus)) {
      return expensesHelpers.getReviewStatusLabel(reviewStatus, t);
    }
    return expensesHelpers.getPolicyStatusLabel(expense?.policy_status, t);
  },

  getExpenseInvoiceMatchType: (expense) => {
    if (!expense) return null;
    const status = expense?.expense_invoice_status;
    const expenseType = expense?.expense_type;

    if (expenseType === null || expenseType === expenseTypesConstants.NO_RECEIPT) return ;

    return {
      [expenseInvoicesStatusConstants.CONFIRMED]: <Invoice2Icon />,
      [expenseInvoicesStatusConstants.MATCHED]: <Invoice2Icon />,
      [expenseInvoicesStatusConstants.PENDING]: <InvoicePendingIcon />,
    }[status] || <InvoiceSuggestIcon />;
  },
  getExpenseTypeInfo: (expense) => {
    if (!expense || typeof expense !== 'object') return null;
    return expense?.policy_expense_type || '';
  },
  getExpensePaymentMethodIcon: (expense, t) => {
    if (!expense || typeof expense !== 'object') return null;

    const paymentMethod = {
      [expensePaymentMethodConstants.CARD]: {
        icon: <WithLessCreditCardIcon />,
        name: 'card',
      },
      [expensePaymentMethodConstants.CASH]: {
        icon: <WithLessOutsideIcon />,
        name: 'cash',
      },
      [expensePaymentMethodConstants.WIRE]: {
        icon: <WireIcon />,
        name: 'wire',
      }
    }[expense?.payment_method] || undefined;

    if (!paymentMethod) return null;

    return (
      <Tooltip title={t(`paymentMethods.${paymentMethod.name}`)}>
        <IconWrapper>{paymentMethod.icon}</IconWrapper>
      </Tooltip>
    )
  },
  isDefinedExpenseType: (expense) => objectHelpers.isValidValue(expense?.expense_type),
  isExpenseInReview: (expense) => expense?.review_status === expenseReviewStatusConstants.IN_REVIEW,
  isEmployeeInExpenseApprovalList: (expense, employeeEmail) => {
    if (!expense || !employeeEmail) return false;
    return (expense?.approval || []).some(review => review.approver?.email === employeeEmail);
  },
  isExpenseAlreadyApprovedByEmployee: (expense, employeeEmail) => {
    if (!expense || !employeeEmail) return false;
    return (expense?.approval || []).some(review => review.approver?.email === employeeEmail && Boolean(review?.approval_date));
  },
  isExpenseAlreadyApprovedByAdmin: (expense) => {
    if (!expense) return false;
    return (expense?.approval || []).some(review => review?.admin_override && Boolean(review?.approval_date));
  },
  isExpenseMissingInfo: (expense) => {
    return (
      expense?.policy_status === expensePolicyStatusConstants.MISSING_INFO &&
      expense?.review_status === expenseReviewStatusConstants.IN_REVIEW
    );
  },
  isAvailableApproveActions: ({employeeEmail, expense, isAdmin = false}) => {
    const isInReviewStatus = expensesHelpers.isExpenseInReview(expense);
    const isInApprovalList = expensesHelpers.isEmployeeInExpenseApprovalList(expense, employeeEmail);
    const isAlreadyApproved = expensesHelpers.isExpenseAlreadyApprovedByEmployee(expense, employeeEmail);

    return isInReviewStatus && !isAlreadyApproved && (isAdmin || isInApprovalList);
  },
  isAnyAdminApproval: (expense) => {
    if (!expense || !Array.isArray(expense.approval)) return false;
    return expense.approval.some(review => review.approver?.email === 'any_admin');
  },
  getAdminOverrideApproval: (expense) => {
    if (!expense || !Array.isArray(expense.approval)) return null;
    return (expense?.approval || []).find(review => review?.admin_override && Boolean(review?.approval_date)) || null;
  },

  handleExpenseError: ({response, fallbackKey, t}) => {
    const message = response?.data?.message || response?.response?.data?.message || t(`${`expenses:validation.${fallbackKey}`}`);
    alertActions.error(message);
  },
  canQuickApprove: ({ expense, employeeEmail, isAdmin = true }) => {
    if (!expense) return false;
    if (isAdmin && expensesHelpers.isAnyAdminApproval(expense)) return true;
    return expense?.approval?.length === 1 && expense.approval[0]?.email === employeeEmail;
  },
}
